/** Session keys for admin-suspended / delete-request notice gating. */
export const ACCOUNT_NOTICE_KEY = "accountNotice";
export const ACCOUNT_NOTICE_REASON_KEY = "accountNoticeReason";

export type AccountNoticeType = "suspended" | "delete";

export interface AccountStatusFlags {
  isActive?: boolean;
  isSuspended?: boolean;
  suspendedReason?: string | null;
}

/**
 * Classify RefreshTokenV2 / login profile flags and persist the notice mode.
 * Suspension wins over delete-request. Normal accounts clear any stored notice.
 */
export const applyAccountNoticeFromResult = (
  result: AccountStatusFlags | null | undefined
): AccountNoticeType | null => {
  const logdetails: string[] = ["action=applyAccountNoticeFromResult"];

  if (!result) {
    clearAccountNotice();
    logdetails.push("status=no_result", "notice=cleared");
    console.log("[accountNotice]", logdetails.join(" | "));
    return null;
  }

  const isSuspended = result.isSuspended === true;
  const isActive = result.isActive === true;
  logdetails.push(`isSuspended=${isSuspended}`, `isActive=${isActive}`);

  if (isSuspended) {
    sessionStorage.setItem(ACCOUNT_NOTICE_KEY, "suspended");
    const reason =
      typeof result.suspendedReason === "string"
        ? result.suspendedReason.trim()
        : "";
    if (reason) {
      sessionStorage.setItem(ACCOUNT_NOTICE_REASON_KEY, reason);
      logdetails.push("hasReason=true");
    } else {
      sessionStorage.removeItem(ACCOUNT_NOTICE_REASON_KEY);
      logdetails.push("hasReason=false");
    }
    logdetails.push("notice=suspended");
    console.log("[accountNotice]", logdetails.join(" | "));
    return "suspended";
  }

  if (!isActive) {
    sessionStorage.setItem(ACCOUNT_NOTICE_KEY, "delete");
    sessionStorage.removeItem(ACCOUNT_NOTICE_REASON_KEY);
    logdetails.push("notice=delete");
    console.log("[accountNotice]", logdetails.join(" | "));
    return "delete";
  }

  clearAccountNotice();
  logdetails.push("notice=cleared", "status=normal");
  console.log("[accountNotice]", logdetails.join(" | "));
  return null;
};

export const getAccountNoticeType = (): AccountNoticeType | null => {
  const value = sessionStorage.getItem(ACCOUNT_NOTICE_KEY);
  if (value === "suspended" || value === "delete") return value;
  return null;
};

export const getAccountNoticeReason = (): string => {
  return sessionStorage.getItem(ACCOUNT_NOTICE_REASON_KEY) || "";
};

export const clearAccountNotice = (): void => {
  sessionStorage.removeItem(ACCOUNT_NOTICE_KEY);
  sessionStorage.removeItem(ACCOUNT_NOTICE_REASON_KEY);
};

/** Persist JWT returned by cancel-deletion (string) or refresh/login (object.token). */
export const storeTokenFromUserRequestResult = (result: unknown): void => {
  const logdetails: string[] = ["action=storeTokenFromUserRequestResult"];
  if (typeof result === "string" && result.trim()) {
    localStorage.setItem("Token", result.trim());
    logdetails.push("source=string", "status=ok");
    console.log("[accountNotice]", logdetails.join(" | "));
    return;
  }
  if (result && typeof result === "object") {
    const token = (result as { token?: string }).token;
    if (typeof token === "string" && token.trim()) {
      localStorage.setItem("Token", token.trim());
      logdetails.push("source=object.token", "status=ok");
      console.log("[accountNotice]", logdetails.join(" | "));
      return;
    }
  }
  logdetails.push("status=no_token");
  console.log("[accountNotice]", logdetails.join(" | "));
};
